/**
 * The shape the phone hands over, and the only shape this portal will render.
 *
 * Two transports carry it (a live data channel, or a file she moves herself) and both land here,
 * so the panels never learn which door the data came through. That is deliberate: a portal that
 * renders one way for the "real" transport and another for the fallback would quietly make the
 * fallback second class, and the fallback is the one that works on a locked-down laptop.
 *
 * **Nothing in this file may be sent anywhere.** It is read into a tab's memory, drawn, and
 * dropped when the tab closes. There is no cache, no localStorage, no service worker, and no
 * PocketBase call that carries any of it. See PRODUCT.md, "Personal things stay personal".
 */

/** Bumped when a field's meaning changes, never when one is added. */
export const PAYLOAD_VERSION = 1

/** What the phone says a day was. Mirrors the app's CycleDay, minus anything it does not send. */
export interface LinkCycleDay {
  /** Days since the Unix epoch, matching the app's primary key. */
  epochDay: number
  /** One of the app's FlowLevel names. */
  flow: string
  symptoms: string[]
  /** Present only when she recorded one. Never defaulted to a "normal" reading. */
  cervicalFluid?: string
  basalTempF?: number
  energy?: string
  /**
   * Her own note, and the single most private string in the product.
   *
   * The phone decides whether to include notes at all; the portal never asks for them. When the
   * export was made without them the field is simply absent, which is not an error state and
   * must not be drawn as one.
   */
  note?: string
}

export interface LinkSupplement {
  id: number
  name: string
  brand: string
  /** A GelatinSource name from the app. */
  gelatin: string
  fastSafe: boolean
  category?: string
  form?: string
  timing?: string
  servingSize?: string
  warnings?: string
  pillsRemaining?: number
  pillsPerDose: number
  lowStockThreshold: number
  /** Pills taken today, already summed by the phone from its own log rows. */
  pillsToday: number
}

export interface LinkCycleSummary {
  /** Null when she has not logged enough for the app to predict anything. */
  averageCycleDays?: number
  averagePeriodDays?: number
  /** Days since the epoch. */
  predictedNextStartEpochDay?: number
  ovulationEpochDay?: number
  fertileStartEpochDay?: number
  fertileEndEpochDay?: number
}

/**
 * The Leviticus 15 count, exactly as the phone computed it.
 *
 * Recomputed nowhere here. The app owns this reckoning and a second implementation in the
 * browser would eventually disagree with it, which for this particular count would be worse
 * than showing nothing: two answers about whether a woman is in her set-apart days is not a
 * rounding difference.
 */
export interface LinkPurity {
  /** 'niddah' | 'prolonged' | 'clean-count' | 'complete', or absent when no count is running. */
  state?: string
  dayNumber?: number
  /** Whether she keeps the count at all. False means the panel is not drawn. */
  enabled: boolean
  /** 'historical' | 'observational', so the portal uses the same voice the app does. */
  framing: string
}

export interface LinkPayload {
  version: number
  /** Milliseconds since the epoch, at the moment the phone built this. */
  exportedAtMillis: number
  /** What she calls the phone, so the portal can say which device it is showing. */
  deviceLabel?: string
  /** The biblical day, from Torahly, so the portal never reckons a date the app already knows. */
  biblical?: {
    month: number
    day: number
    isSabbath: boolean
    feastName?: string
    upcomingFeasts?: { name: string; epochDay: number }[]
  }
  cycleDays: LinkCycleDay[]
  cycle: LinkCycleSummary
  purity: LinkPurity
  supplements: LinkSupplement[]
}

export class PayloadError extends Error {}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

function num(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

/**
 * Turns whatever arrived into a payload, or refuses it.
 *
 * Strict on the way in on purpose. This is the one place data crosses from a device the portal
 * does not control, and a lenient parser here is how a malformed export becomes a blank panel
 * with no explanation. Unknown fields are dropped rather than carried, so a newer phone talking
 * to an older portal degrades to what this version understands instead of leaking fields nothing
 * has been designed to render.
 */
export function parsePayload(raw: unknown): LinkPayload {
  if (!isRecord(raw)) throw new PayloadError('That file is not a Covellah export.')

  const version = num(raw.version)
  if (version === undefined) throw new PayloadError('That file is not a Covellah export.')
  if (version > PAYLOAD_VERSION) {
    throw new PayloadError(
      'That export came from a newer version of the app than this page understands. Update the portal, or export again from an older phone.',
    )
  }

  const exportedAtMillis = num(raw.exportedAtMillis)
  if (exportedAtMillis === undefined) {
    throw new PayloadError('That export is missing the time it was made, so it cannot be trusted.')
  }

  const cycleDaysRaw = Array.isArray(raw.cycleDays) ? raw.cycleDays : []
  const cycleDays: LinkCycleDay[] = []
  for (const entry of cycleDaysRaw) {
    if (!isRecord(entry)) continue
    const epochDay = num(entry.epochDay)
    if (epochDay === undefined) continue
    cycleDays.push({
      epochDay,
      flow: str(entry.flow) ?? 'NONE',
      symptoms: Array.isArray(entry.symptoms) ? entry.symptoms.filter((s): s is string => typeof s === 'string') : [],
      cervicalFluid: str(entry.cervicalFluid),
      basalTempF: num(entry.basalTempF),
      energy: str(entry.energy),
      note: str(entry.note),
    })
  }

  const supplementsRaw = Array.isArray(raw.supplements) ? raw.supplements : []
  const supplements: LinkSupplement[] = []
  for (const entry of supplementsRaw) {
    if (!isRecord(entry)) continue
    const id = num(entry.id)
    const name = str(entry.name)
    if (id === undefined || name === undefined) continue
    supplements.push({
      id,
      name,
      brand: str(entry.brand) ?? '',
      gelatin: str(entry.gelatin) ?? 'UNKNOWN',
      fastSafe: entry.fastSafe === true,
      category: str(entry.category),
      form: str(entry.form),
      timing: str(entry.timing),
      servingSize: str(entry.servingSize),
      warnings: str(entry.warnings),
      pillsRemaining: num(entry.pillsRemaining),
      pillsPerDose: num(entry.pillsPerDose) ?? 1,
      lowStockThreshold: num(entry.lowStockThreshold) ?? 10,
      pillsToday: num(entry.pillsToday) ?? 0,
    })
  }

  const cycleRaw = isRecord(raw.cycle) ? raw.cycle : {}
  const purityRaw = isRecord(raw.purity) ? raw.purity : {}
  const biblicalRaw = isRecord(raw.biblical) ? raw.biblical : undefined

  return {
    version,
    exportedAtMillis,
    deviceLabel: str(raw.deviceLabel),
    biblical:
      biblicalRaw && num(biblicalRaw.month) !== undefined && num(biblicalRaw.day) !== undefined
        ? {
            month: num(biblicalRaw.month)!,
            day: num(biblicalRaw.day)!,
            isSabbath: biblicalRaw.isSabbath === true,
            feastName: str(biblicalRaw.feastName),
            upcomingFeasts: Array.isArray(biblicalRaw.upcomingFeasts)
              ? biblicalRaw.upcomingFeasts
                  .filter(isRecord)
                  .map((f) => ({ name: str(f.name) ?? '', epochDay: num(f.epochDay) ?? 0 }))
                  .filter((f) => f.name.length > 0)
              : undefined,
          }
        : undefined,
    cycleDays,
    cycle: {
      averageCycleDays: num(cycleRaw.averageCycleDays),
      averagePeriodDays: num(cycleRaw.averagePeriodDays),
      predictedNextStartEpochDay: num(cycleRaw.predictedNextStartEpochDay),
      ovulationEpochDay: num(cycleRaw.ovulationEpochDay),
      fertileStartEpochDay: num(cycleRaw.fertileStartEpochDay),
      fertileEndEpochDay: num(cycleRaw.fertileEndEpochDay),
    },
    purity: {
      state: str(purityRaw.state),
      dayNumber: num(purityRaw.dayNumber),
      enabled: purityRaw.enabled === true,
      framing: str(purityRaw.framing) ?? 'historical',
    },
    supplements,
  }
}
